// ═══════════════════════════════════════════════════════════════
// The code, at sign-in
// ═══════════════════════════════════════════════════════════════
// Shown when this session is owed a step-up: the password was right and a
// verified authenticator exists, so Supabase handed back an aal1 session that
// has not been raised yet. Nothing else in MOS renders until it is.
//
// Sign out is always reachable. Someone on the wrong account, or holding a
// phone that has lost its authenticator, must never be stuck on a screen whose
// only exit is a code they cannot produce.

import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { primaryFactorId, submitChallenge } from '../../utils/mfa';
import { MfaCodeInput } from './MfaCodeInput';

export function MfaChallengeScreen() {
  const { signOut, refreshMfaState, user } = useAuth();
  const [code, setCode] = useState('');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void primaryFactorId()
      .then(setFactorId)
      .catch(() => setError('Could not reach two-factor. Sign out and try again.'));
  }, []);

  const submit = useCallback(() => {
    if (!factorId || code.length !== 6 || busy) return;
    setBusy(true); setError(null);
    void (async () => {
      try {
        await submitChallenge(factorId, code);
        await refreshMfaState();
      } catch (e) {
        // A rejected code is almost always a clock or a stale screen, not a
        // wrong secret. Say the useful thing instead of "invalid".
        setError(
          e instanceof Error && /expired|invalid|not accepted/i.test(e.message)
            ? 'That code did not work. Codes change every 30 seconds — wait for the next one and type it fresh.'
            : e instanceof Error ? e.message : 'That code was not accepted',
        );
        setCode('');
        setBusy(false);
      }
    })();
  }, [factorId, code, busy, refreshMfaState]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="w-full max-w-sm bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
        <ShieldCheck className="w-8 h-8 text-blue-600 mx-auto" />
        <h1 className="mt-4 text-lg font-semibold text-gray-900 text-center">
          Enter your code
        </h1>
        <p className="mt-1.5 text-sm text-gray-500 text-center leading-relaxed">
          Open your authenticator app and type the six digits for {user?.email}.
        </p>

        <div className="mt-6">
          <MfaCodeInput
            value={code}
            onChange={setCode}
            onComplete={submit}
            disabled={busy || !factorId}
            autoFocus
          />
        </div>

        {error && <p className="mt-3 text-sm text-red-600 leading-relaxed">{error}</p>}

        <button
          type="button"
          onClick={submit}
          disabled={busy || code.length !== 6 || !factorId}
          className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                     bg-blue-600 text-white text-sm font-medium hover:bg-blue-700
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {busy ? 'Checking…' : 'Continue'}
        </button>

        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-3 w-full px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
